import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    Home as HomeIcon,
    Mail as MailIcon,
    SwapHoriz as SwapHorizIcon,
    ReportProblem as ReportProblemIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    LocationOn as LocationOnIcon,
    Group as GroupIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocList = [
    { id: 'introduction', label: 'はじめに ― ルームシェアと手続きの意外な落とし穴' },
    { id: 'move-residence-card', label: '住民票の移動方法と実務ポイント' },
    { id: 'receive-mail', label: '郵便物の受け取りと賢い住所利用術' },
    { id: 'address-change', label: 'ルームシェアの住所変更で絶対注意すべきポイント' },
    { id: 'trouble-cases', label: '実際にあったトラブル事例と回避策' },
    { id: 'expert-tips', label: '専門家のアドバイス＆ルームシェア成功のコツ' },
    { id: 'conclusion', label: 'まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocList.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold',
                                        borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに ― ルームシェアと手続きの意外な落とし穴
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、家賃の節約だけでなく、新しい人間関係やライフスタイルを楽しめる現代的な住まい方です。しかし、<b>引っ越し後に直面する「住民票」や「郵便物」の手続き</b>は、意外と落とし穴が多いもの。<Chip label="住民票" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />や<Chip label="郵便物" color="primary" variant="outlined" size="small" sx={{ mx: 0.5 }} />の住所変更を怠ると、思わぬトラブルにつながる場合も…。<br />
                    この記事では、<b>ルームシェア生活で絶対に押さえておきたい手続きのポイント</b>と、<b>実際のトラブル事例・専門的なアドバイス</b>を、最新の知識と豊富な実例を交えて徹底解説します。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f5fafd', borderLeft: '5px solid #64b5f6' }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                                こんな人におすすめ！
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="これからルームシェアを始める予定" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="住民票や郵便物の手続きに不安がある" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="トラブルを避けて快適なシェア生活を送りたい" />
                                </ListItem>
                            </List>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* 住民票移動 */}
            <Box id="move-residence-card" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    住民票の移動方法と実務ポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                ルームシェアで新しい住所に住み始めたら、<b>原則として住民票は移動が必要</b>です。
                                住民票の移動は法律上の義務であり、免許証や銀行口座、保険証など多くの手続きにも関わってきます。
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                【住民票の移動手続きの流れ】
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><LocationOnIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="新住所へ引っ越した日から14日以内に市区町村役場で手続き" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="本人確認書類（運転免許証・マイナンバーカード等）を持参" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><SwapHorizIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="場合によっては転出証明書（前住所で取得）が必要" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="ルームシェアの場合は「○○様方」や世帯主名も正確に記載" />
                                </ListItem>
                            </List>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <img
                                    src="/images/blog/residence-card-counter_roomshare-juminhyo-yubin-guide.jpg"
                                    alt="市役所の住民票窓口で住民票移動の手続きをする若い男女。カウンターに並ぶ人々、明るい青と白の内装、親切な窓口職員の様子"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Paper elevation={1} sx={{ p: 2, bgcolor: '#f7fafd', borderRadius: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                        ルームシェア住民票でよくある疑問
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><InfoIcon color="info" /></ListItemIcon>
                            <ListItemText
                                primary="「ルームシェアでも住民票は移せる？」"
                                secondary="→ はい、名義人（世帯主）や管理会社の承諾があれば原則OK。契約内容も確認しましょう。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><InfoIcon color="info" /></ListItemIcon>
                            <ListItemText
                                primary="「世帯主との続柄はどう書く？」"
                                secondary="→ 友人同士なら「同居人」、家族なら「子」や「配偶者」など。世帯分離も可能です。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2">
                    住民票を移動しないと、行政サービスが受けられなかったり、選挙の投票案内が届かなくなるなど暮らしに支障が出ます。<b>面倒でも早めに手続きを済ませましょう！</b>
                </Typography>
            </Box>

            {/* 郵便物の受け取り */}
            <Box id="receive-mail" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    郵便物の受け取りと賢い住所利用術
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>

                        </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            ルームシェアで最も多いトラブルの一つが<b>郵便物の紛失や誤配</b>です。複数人で住所を共有するため、<b>宛名や部屋番号、世帯主名の記載</b>に気を配る必要があります。
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><MailIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="「○○様方」「部屋番号」などを明記"
                                    secondary="郵便物の宛先に世帯主やシェアハウス名を記載すると誤配リスクが減少"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><MailIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="重要書類・荷物は「書留」や追跡サービスを活用"
                                    secondary="特に転送届を出す場合、必ず追跡できる方法を選びましょう"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            郵便局への転送届も、旧住所→新住所へ忘れずに提出しましょう！
                        </Typography>
                    </Grid>
                </Grid>
                <Paper elevation={0} sx={{ mt: 2, bgcolor: '#fffde7', p: 2, borderRadius: 1, borderLeft: '5px solid #ffb300' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff8f00', mb: 1 }}>
                        【ワンポイントアドバイス】
                    </Typography>
                    <Typography variant="body2">
                        ルームシェアでは、<b>宅配ボックスや共有の郵便受けに暗証番号</b>を設定するのもおすすめ。<br />
                        また、頻繁に届く通販やサブスク荷物は「受取人名＋部屋番号」で登録しておくとスムーズです。
                    </Typography>
                </Paper>
            </Box>

            {/* 住所変更の注意点 */}
            <Box id="address-change" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの住所変更で絶対注意すべきポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    住民票や郵便物だけでなく、<b>銀行・クレジットカード・運転免許証・保険・携帯電話</b>など、日常生活に関わるあらゆるサービスの住所変更も忘れずに行いましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><SwapHorizIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="各種サービスの住所更新"
                                    secondary="金融機関・クレカ・保険・通販サイト・役所関係など、リストアップして一括対応が安心"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ReportProblemIcon color="error" /></ListItemIcon>
                                <ListItemText
                                    primary="ルームメイトの情報と混同しない"
                                    secondary="同じ住所内で名前が似ている場合、誤配・手続きミスが起こりやすい"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                <ListItemText
                                    primary="公共料金等の名義・支払方法も要確認"
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#f5fafd', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                                【住所変更チェックリスト】
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="銀行・証券口座" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="クレジットカード会社" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="運転免許証・マイナンバーカード" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="保険（健康・自動車・生命等）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="携帯電話・プロバイダー" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="通販サイト（Amazon、楽天等）" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="役所・税務署・年金機構" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    住所変更はまとめて済ませるのがコツ。<b>チェックリストを作成し、1つずつ対応</b>しましょう！
                </Typography>
            </Box>

            {/* トラブル事例 */}
            <Box id="trouble-cases" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    実際にあったトラブル事例と回避策
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            ルームシェアの現場では、<b>「うっかり」が大きなトラブルに発展</b>することも。ここではよくある事例と、避けるためのポイントを紹介します。
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><ReportProblemIcon color="error" /></ListItemIcon>
                                <ListItemText
                                    primary="住民票未移動で行政サービスが利用できなくなった"
                                    secondary="→ 転入届を出さずにいたため、健康保険やマイナンバーカードの更新ができず困った…"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ReportProblemIcon color="error" /></ListItemIcon>
                                <ListItemText
                                    primary="郵便物の誤配・紛失で重要書類が届かない"
                                    secondary="→ 共有ポストで他の住人と混ざり、通知書の紛失やカードの不正利用リスクに…"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ReportProblemIcon color="error" /></ListItemIcon>
                                <ListItemText
                                    primary="ルームメイトの情報と混同し金融機関の手続きが遅延"
                                    secondary="→ 住所に複数名義があり、本人確認に手間取り振込が遅延"
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>

                        </Box>
                        <Paper elevation={0} sx={{ bgcolor: '#ffebee', p: 2, borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#c62828', mb: 1 }}>
                                【トラブル防止のためのチェックポイント】
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="住民票・郵便物の手続きは必ず全員で確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="重要書類は個人保管＆受領サインを徹底" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 専門家アドバイス */}
            <Box id="expert-tips" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    専門家のアドバイス＆ルームシェア成功のコツ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            これまで多くのシェアハウス・ルームシェア物件のサポートをしてきた経験から、<b>「手続きトラブル」を避けるコツ</b>をまとめました。
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><GroupIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="ルームメイト同士で「手続きリスト」を共有" secondary="グループLINEやGoogleドキュメントで進捗を見える化" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><GroupIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="物件オーナーや管理会社に事前相談" secondary="住民票移動や郵便受けの名義など、事前に確認しておくと安心" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><GroupIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="郵便物の「引き取りルール」を決める" secondary="誰がどのように受け取るか、紛失時の対応などを事前に決めておく" />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#f1f8e9', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#43a047', mb: 1 }}>
                                【プロのワンポイント】
                            </Typography>
                            <Typography variant="body2">
                                「手続きの証拠」をスクリーンショットや写真で残しておくと、万が一トラブルが起きても証明しやすくなります。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* まとめ */}
            <Box id="conclusion" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活を快適に過ごすためには、<b>住民票や郵便物、各種手続きのポイントを押さえ、ルームメイトと情報共有</b>することが大切です。<br />
                    手続きはやや面倒ですが、<b>最初にしっかり対応しておけば、その後の暮らしがグッと快適に</b>なります。
                </Typography>
                <Paper elevation={0} sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        ルームシェアの手続きで失敗しないために
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="住民票は必ず新住所へ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="郵便物の宛名・管理ルールを明確に" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="住所変更リストを作成し、全員と共有" />
                        </ListItem>
                    </List>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-juminhyo-yubin-guide',
    slug: 'roomshare-juminhyo-yubin-guide',
    title: 'ルームシェアの「住民票・郵便物」手続き完全ガイド',
    excerpt: '住民票移動方法\n郵便物受け取り\n住所変更注意点\nトラブル事例',
    coverImage: '/images/blog/cover-roomshare-juminhyo-yubin_roomshare-juminhyo-yubin-guide.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木製のテーブルとカラフルなインテリア、小物が配置され、フレンドリーで安心感のある雰囲気が伝わる',
    publishedAt: '2024-06-09T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '生活手続き', '住民票・郵便物'],
    tags: ['住民票', '郵便物', '住所変更', 'トラブル事例', 'ルームシェア手続き'],
    author: {
        name: '山本 直樹',
        avatar: '/images/avatars/naoki-yamamoto.jpg',
        bio: 'ルームシェアやシェアハウスの現場を10年以上サポート。最新の住まい事情やトラブル回避策、快適な共同生活の知識を幅広く発信しています。',
        profilePicture: '/images/avatars/naoki-yamamoto.jpg'
    },
    viewCount: 1780,
    contentComponent: PostContent
};

export default post;